/**
 * Mini Sankey Layout Utilities
 * WP-DASH-2: Layout computation for dashboard mini Sankey visualization
 *
 * Provides simplified layout calculations optimized for the dashboard card size.
 * Uses the same color system as the full flow visualization but with compact rendering.
 */

import type { InflowNode, AccountNode, OutflowNode, FlowEdge } from '@/components/flow/types';

// ============================================================================
// Types
// ============================================================================

export interface CanvasDimensions {
  width: number;
  height: number;
  padding: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

export interface Position {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PositionedNode extends Position {
  id: string;
  label: string;
  value: number;
  /** For account nodes, this is the balance */
  balance?: number;
}

export interface MiniLayout {
  inflowX: number;
  accountX: number;
  outflowX: number;
  columnWidth: number;
  inflowNodes: PositionedNode[];
  accountNodes: PositionedNode[];
  outflowNodes: PositionedNode[];
  nodePositions: Map<string, Position>;
}

// ============================================================================
// Constants
// ============================================================================

/** Default canvas dimensions for mini Sankey */
export const MINI_CANVAS: CanvasDimensions = {
  width: 440,
  height: 280,  // Increased from 260 to accommodate 5 nodes without clipping
  padding: { top: 32, bottom: 24, left: 16, right: 16 },
};

/** Node sizing constraints */
export const NODE_SIZING = {
  width: 90,
  minHeight: 28,
  maxHeight: 44,  // Reduced from 52 to fit more nodes in available space
  gap: 8,
  borderRadius: 4,
};

/** Maximum nodes per column in mini view */
export const MAX_NODES_PER_COLUMN = 5;

/** Edge styling */
export const EDGE_STYLING = {
  minStrokeWidth: 2,
  maxStrokeWidth: 10,
  opacity: 0.4,
  hoverOpacity: 0.7,
  curvature: 0.5,
};

/** Color palette for mini Sankey (matches flow constants) */
export const MINI_FLOW_COLORS = {
  inflow: {
    background: '#DCFCE7', // green-100
    border: '#16A34A', // green-600
    text: '#166534', // green-800
  },
  account: {
    background: '#DBEAFE', // blue-100
    border: '#2563EB', // blue-600
    text: '#1E40AF', // blue-800
  },
  outflow: {
    background: '#FEE2E2', // red-100
    border: '#DC2626', // red-600
    text: '#991B1B', // red-800
  },
  edge: {
    income: 'rgba(22, 163, 74, 0.35)', // green with opacity
    expense: 'rgba(220, 38, 38, 0.35)', // red with opacity
    transfer: 'rgba(107, 114, 128, 0.35)', // gray with opacity
  },
};

// ============================================================================
// Layout Functions
// ============================================================================

/**
 * Compute the height of a node based on its value relative to the max value
 */
function computeNodeHeight(value: number, maxValue: number): number {
  if (maxValue <= 0) return NODE_SIZING.minHeight;

  const ratio = Math.min(value / maxValue, 1);
  const range = NODE_SIZING.maxHeight - NODE_SIZING.minHeight;

  return Math.round(NODE_SIZING.minHeight + ratio * range);
}

/**
 * Position nodes vertically within a column, centered as a group
 */
function layoutColumn<T extends { id: string; name: string; value?: number; balance?: number }>(
  nodes: T[],
  columnX: number,
  usableHeight: number,
  topPadding: number
): PositionedNode[] {
  if (nodes.length === 0) return [];

  // Get the value to use for sizing (value for income/expense, flowTotal for accounts)
  const getNodeValue = (node: T): number => {
    // For account nodes, use balance for display but value for sizing if available
    if ('balance' in node && node.balance !== undefined) {
      return Math.abs(node.balance);
    }
    return Math.abs(node.value ?? 0);
  };

  // Find max value for proportional sizing
  const maxValue = Math.max(...nodes.map(getNodeValue), 1);

  // Calculate heights
  const nodeHeights = nodes.map((node) => computeNodeHeight(getNodeValue(node), maxValue));

  // Calculate total height needed
  const totalHeight = nodeHeights.reduce((sum, h) => sum + h, 0) + NODE_SIZING.gap * (nodes.length - 1);

  // Calculate starting Y position (centered in available space)
  const startY = topPadding + Math.max(0, (usableHeight - totalHeight) / 2);

  // Position each node
  let currentY = startY;
  const positioned: PositionedNode[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (!node) continue;

    const height = nodeHeights[i] ?? NODE_SIZING.minHeight;

    positioned.push({
      id: node.id,
      label: node.name,
      value: node.value ?? 0,
      balance: 'balance' in node ? (node.balance as number) : undefined,
      x: columnX,
      y: currentY,
      width: NODE_SIZING.width,
      height,
    });

    currentY += height + NODE_SIZING.gap;
  }

  return positioned;
}

/**
 * Compute layout for the mini Sankey diagram
 *
 * @param inflowNodes - Income source nodes
 * @param accountNodes - Account nodes
 * @param outflowNodes - Expense category nodes
 * @param canvas - Canvas dimensions
 * @returns Layout with positioned nodes and column X coordinates
 */
export function computeMiniLayout(
  inflowNodes: InflowNode[],
  accountNodes: AccountNode[],
  outflowNodes: OutflowNode[],
  canvas: CanvasDimensions
): MiniLayout {
  const usableWidth = canvas.width - canvas.padding.left - canvas.padding.right;
  const usableHeight = canvas.height - canvas.padding.top - canvas.padding.bottom;

  // Calculate column X positions (20% column width, 15% gaps, 10% margins)
  // Layout: 10% | 20% column | 15% gap | 20% column | 15% gap | 20% column | 10%
  const columnWidth = usableWidth * 0.25;
  const gapWidth = usableWidth * 0.125;

  const inflowX = canvas.padding.left;
  const accountX = inflowX + columnWidth + gapWidth;
  const outflowX = accountX + columnWidth + gapWidth;

  // Position nodes in each column
  const positionedInflows = layoutColumn(inflowNodes, inflowX, usableHeight, canvas.padding.top);
  const positionedAccounts = layoutColumn(
    accountNodes.map((a) => ({ ...a, value: a.flowTotal })),
    accountX,
    usableHeight,
    canvas.padding.top
  );
  const positionedOutflows = layoutColumn(outflowNodes, outflowX, usableHeight, canvas.padding.top);

  // Build node position map for edge routing
  const nodePositions = new Map<string, Position>();

  for (const node of [...positionedInflows, ...positionedAccounts, ...positionedOutflows]) {
    nodePositions.set(node.id, {
      x: node.x,
      y: node.y,
      width: node.width,
      height: node.height,
    });
  }

  return {
    inflowX,
    accountX,
    outflowX,
    columnWidth,
    inflowNodes: positionedInflows,
    accountNodes: positionedAccounts,
    outflowNodes: positionedOutflows,
    nodePositions,
  };
}

// ============================================================================
// Edge Functions
// ============================================================================

/**
 * Compute a cubic Bezier path between two nodes
 *
 * @param source - Source node position
 * @param target - Target node position
 * @param curvature - Bezier control point factor (0-1)
 * @returns SVG path string
 */
export function computeCubicBezierPath(
  source: Position,
  target: Position,
  curvature: number = EDGE_STYLING.curvature
): string {
  // Source anchor: right edge, vertical center
  const x0 = source.x + source.width;
  const y0 = source.y + source.height / 2;

  // Target anchor: left edge, vertical center
  const x1 = target.x;
  const y1 = target.y + target.height / 2;

  // Control points for smooth curve
  const dx = (x1 - x0) * curvature;
  const cx0 = x0 + dx;
  const cx1 = x1 - dx;

  return `M ${x0} ${y0} C ${cx0} ${y0}, ${cx1} ${y1}, ${x1} ${y1}`;
}

/**
 * Calculate stroke width for an edge based on its value
 *
 * @param value - Edge value (transaction amount)
 * @param maxValue - Maximum edge value for normalization
 * @returns Stroke width in pixels
 */
export function computeEdgeStrokeWidth(value: number, maxValue: number): number {
  if (maxValue <= 0) return EDGE_STYLING.minStrokeWidth;

  const ratio = Math.min(value / maxValue, 1);
  const range = EDGE_STYLING.maxStrokeWidth - EDGE_STYLING.minStrokeWidth;

  return Math.round(EDGE_STYLING.minStrokeWidth + ratio * range);
}

/**
 * Get the color for an edge based on its type
 */
export function getEdgeColor(edge: FlowEdge): string {
  switch (edge.type) {
    case 'income':
      return MINI_FLOW_COLORS.edge.income;
    case 'expense':
      return MINI_FLOW_COLORS.edge.expense;
    case 'transfer':
      return MINI_FLOW_COLORS.edge.transfer;
    default:
      return MINI_FLOW_COLORS.edge.expense;
  }
}

/**
 * Filter edges to only include those connecting visible nodes
 */
export function filterVisibleEdges(
  edges: FlowEdge[],
  nodePositions: Map<string, Position>
): FlowEdge[] {
  return edges.filter(
    (edge) => nodePositions.has(edge.source) && nodePositions.has(edge.target)
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Truncate a string to a maximum length with ellipsis
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 1) + '\u2026'; // Unicode ellipsis
}

/**
 * Format a currency value in compact notation
 * e.g., $1,234 -> $1.2K, $1,234,567 -> $1.2M
 */
export function formatCompactCurrency(value: number): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000) {
    return `${sign}$${(absValue / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}$${(absValue / 1_000).toFixed(1)}K`;
  }

  return `${sign}$${Math.round(absValue).toLocaleString()}`;
}
